import { createSlice } from '@reduxjs/toolkit'
import { adminLoginThunk, verifyOTPThunk } from './adminThunk'

interface AdminProfile {
  email: string
  name: string
  userId: string
}

interface AdminAuthState {
  adminInfo: AdminProfile | null // Admin user information
  token: string | null // Admin authentication token
  isLoading: boolean
  error: string | null // To keep error handling consistent, using string | null
  otpSent: boolean // To track if OTP has been sent
}

// Load initial state from localStorage
const loadInitialState = (): AdminAuthState => {
  const storedToken = localStorage.getItem('adminToken')
  const storedAdminInfo = localStorage.getItem('adminInfo')
  
  return {
    adminInfo: storedAdminInfo ? JSON.parse(storedAdminInfo) : null,
    token: storedToken || null,
    isLoading: false,
    error: null,
    otpSent: false,
  }
}

const initialState: AdminAuthState = loadInitialState()

export const adminAuthSlice = createSlice({
  name: 'adminAuth',
  initialState,
  reducers: {
    // Function to handle admin logout
    logoutAdmin: (state) => {
      state.adminInfo = null
      state.token = null
      state.error = null
      state.otpSent = false
      // Clear localStorage
      localStorage.removeItem('adminToken')
      localStorage.removeItem('adminInfo')
    },
    clearError: (state) => {
      state.error = null
    },
    resetOTPState: (state) => {
      state.otpSent = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(adminLoginThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(adminLoginThunk.fulfilled, (state, action) => {
        state.isLoading = false
        // OTP disabled - direct login stores user and token immediately
        state.adminInfo = action.payload.user
        state.token = action.payload.token
        state.otpSent = false
        
        // Persist to localStorage
        if (action.payload.token) {
          localStorage.setItem('adminToken', action.payload.token)
        }
        if (action.payload.user) {
          localStorage.setItem('adminInfo', JSON.stringify(action.payload.user))
        }
      })
      .addCase(adminLoginThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(verifyOTPThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(verifyOTPThunk.fulfilled, (state, action) => {
        state.adminInfo = action.payload.user // Assuming the payload includes the admin user info
        state.token = action.payload.token // Store the token
        state.isLoading = false
        state.otpSent = false // Reset OTP sent status after successful verification
        
        // Persist to localStorage
        if (action.payload.token) {
          localStorage.setItem('adminToken', action.payload.token)
        }
        if (action.payload.user) {
          localStorage.setItem('adminInfo', JSON.stringify(action.payload.user))
        }
      })
      .addCase(verifyOTPThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string // Assuming error payload is a string
      })
  },
})

export const { logoutAdmin, clearError, resetOTPState } = adminAuthSlice.actions

export default adminAuthSlice.reducer
